import CabecalhoPaginaSecundaria from "../../../../componentes/comuns/CabecalhoPaginaSecundaria";
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CardContent,
  Typography,
  Dialog,
  IconButton,
  Box,
  Grid,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import EdicaoInfo from "./editarInfo";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";
import { api } from "@api/api";
import DialogRecusaCadastro from "./dialogRecusaCadastro";
import FeedbackInline from "@componentes/feedback/FeedbackInline";
import {
  ajustarTamanhoFonteAnalista,
  tamanhosGlobais,
} from "../../../../estilos/global";

const colors = {
  primary: "#1976d2",
  success: "#4caf50",
  error: "#f44336",
  warning: "#ff9800",
  background: "var(--sear-superficie-suave, #f5f5f5)",
  text: "var(--sear-texto, #333333)",
  accent: "#6c5ce7",
};

const maskCNPJ = (value) => {
  if (!value) return "";

  return value
    .replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .replace(/(-\d{2})\d+?$/, "$1");
};

const maskPhone = (value) => {
  if (!value) return "";

  return value
    .replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(-\d{4})\d+?$/, "$1");
};

const maskCEP = (value) => {
  if (!value) return "";

  return value
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(-\d{3})\d+?$/, "$1");
};

const StyledCard = styled(motion.div)`
  max-width: 100%;
  margin: 0;
  padding: 16px;
  background: var(--sear-superficie, white);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  display: grid;
  grid-template-columns: minmax(0, 1fr);
  column-gap: 12px;
  align-items: start;

  > .MuiIconButton-root {
    margin-bottom: 0;
  }

  > .MuiCardContent-root,
  > .MuiCardContent-root:last-child {
    min-width: 0;
    padding: 0;
  }

  .MuiGrid-container {
    margin-top: 0;
  }

  .MuiGrid-container > .MuiGrid-item {
    padding-top: 8px;
  }

  .MuiTypography-body1 {
    font-size: ${ajustarTamanhoFonteAnalista("0.875rem")};
  }

  .MuiTypography-h6 {
    font-size: ${ajustarTamanhoFonteAnalista("1rem")};
  }

  .MuiTypography-body2 {
    font-size: ${ajustarTamanhoFonteAnalista("0.8125rem")};
  }

  @media (min-width: 900px) {
    flex: 1 1 auto;
    min-height: min-content;

    > .MuiCardContent-root {
      align-self: stretch;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-height: min-content;
    }

    > .MuiCardContent-root > h3 {
      align-self: flex-start;
    }

    .MuiTypography-body1 {
      font-size: ${ajustarTamanhoFonteAnalista(tamanhosGlobais.textoPadrao)};
    }

    button:not(.MuiIconButton-root) {
      font-size: ${ajustarTamanhoFonteAnalista(tamanhosGlobais.botao)};
    }

    .MuiTypography-h6 {
      font-size: ${ajustarTamanhoFonteAnalista(tamanhosGlobais.tituloSecao)};
    }

    .MuiTypography-body2 {
      font-size: ${ajustarTamanhoFonteAnalista(tamanhosGlobais.textoAuxiliar)};
    }
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 8px;
    background: ${colors.primary};
  }
`;

const StyledButton = styled(motion.button)`
  margin: 0;
  padding: 8px 20px;
  min-height: 36px;
  min-width: 120px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: ${ajustarTamanhoFonteAnalista("0.875rem")};
  font-weight: 600;
  color: white;
  background: ${(props) => props.color || colors.primary};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 1px;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--sear-superficie, rgba(255, 255, 255, 0.2));
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: translateX(0);
  }
`;

const InfoItem = styled(motion.div)`
  padding: 6px 10px;
  margin-bottom: 4px;
  background: var(--sear-superficie-suave, rgba(245, 245, 245, 0.5));
  border-radius: 8px;
  border-left: 4px solid ${colors.accent};

  strong {
    color: ${colors.accent};
  }
`;

const DocumentPreview = styled(motion.div)`
  margin-top: 12px;
  padding: 12px;
  background: rgba(25, 118, 210, 0.05);
  border-radius: 12px;
  border: 2px dashed var(--sear-borda-forte, rgba(25, 118, 210, 0.3));
  text-align: center;
  cursor: pointer;
`;

const MantenedoraInfo = () => {
  const { tipo, id } = useParams();
  const navigate = useNavigate();

  const [mantenedora, setMantenedora] = useState(null);
  const [documentoUrl, setDocumentoUrl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [recusaAberta, setRecusaAberta] = useState(false);
  const [erroDecisao, setErroDecisao] = useState("");
  const [aprovando, setAprovando] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await fetch(api.instData(tipo, id), {
        credentials: "include",
      });

      const data = await response.json();

      setMantenedora(data);

      if (data.documento) {
        const byteCharacters = atob(data.documento);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        setDocumentoUrl(URL.createObjectURL(blob));
      }
    } catch (error) {
      console.error("Erro ao carregar dados da mantenedora:", error);
    } finally {
      setIsLoading(false);
    }
  }, [tipo, id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAprovar = useCallback(async () => {
    if (aprovando) return;

    setAprovando(true);
    setErroDecisao("");

    try {
      const response = await fetch(api.approve(tipo, id), {
        method: "PATCH",
        credentials: "include",
      });

      if (!response.ok) {
        const dados = await response.json().catch(() => ({}));

        throw new Error(
          dados.message || "Não foi possível aprovar o cadastro.",
        );
      }

      navigate(-1);
    } catch (error) {
      setErroDecisao(
        error instanceof Error ? error.message : "Erro ao aprovar cadastro.",
      );
    } finally {
      setAprovando(false);
    }
  }, [tipo, id, navigate, aprovando]);

  const handleRecusar = useCallback(() => {
    setErroDecisao("");
    setRecusaAberta(true);
  }, []);

  const handleEditar = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleSave = useCallback(
    async (updatedData) => {
      try {
        await fetch(api.edit(tipo, id), {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
          credentials: "include",
        });

        setMantenedora(updatedData);
        setIsEditing(false);
      } catch (error) {
        console.error("Erro ao salvar edição:", error);
      }
    },
    [tipo, id],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: colors.background,
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          style={{
            width: 50,
            height: 50,
            border: `5px solid ${colors.primary}`,
            borderTopColor: "transparent",
            borderRadius: "50%",
          }}
        />
      </Box>
    );
  }

  if (!mantenedora) {
    return (
      <Box
        sx={{
          backgroundColor: colors.background,
          minHeight: "100vh",
          padding: "2rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" color={colors.error}>
          Ocorreu um erro ao carregar os dados.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: colors.background,
        minHeight: 0,
        padding: 0,
        position: "relative",
        display: { xs: "block", md: "flex" },
        flexDirection: "column",
        flex: "1 1 auto",
      }}
    >
      <StyledCard
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        

        <CardContent>
          <FeedbackInline mensagem={erroDecisao} />

          <CabecalhoPaginaSecundaria
            titulo={mantenedora.nome}
            sx={{ mb: 2 }}
            onVoltar={() => navigate(-1)}
          />

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Tipo:</strong> Mantenedora
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>CNPJ:</strong> {maskCNPJ(mantenedora.cnpj)}
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Email:</strong> {mantenedora.email}
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Telefone:</strong> {maskPhone(mantenedora.telefone)}
                  </Typography>
                </InfoItem>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Cidade:</strong> {mantenedora.cidade}
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Estado:</strong> {mantenedora.estado}
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Endereço:</strong> {mantenedora.rua},{" "}
                    {mantenedora.numero}, {mantenedora.bairro}
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>CEP:</strong> {maskCEP(mantenedora.cep)}
                  </Typography>
                </InfoItem>
              </motion.div>
            </Grid>
          </Grid>

          {documentoUrl && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <DocumentPreview
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setOpenDialog(true)}
              >
                <Typography variant="h6" sx={{ color: colors.primary, mb: 1 }}>
                  Documento Disponível
                </Typography>

                <Typography variant="body2" sx={{ color: colors.text }}>
                  Clique para visualizar o documento anexado
                </Typography>
              </DocumentPreview>
            </motion.div>
          )}

          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <StyledButton
              color={colors.success}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={aprovando}
              onClick={handleAprovar}
            >
              Aprovar
            </StyledButton>

            <StyledButton
              color={colors.error}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={aprovando}
              onClick={handleRecusar}
            >
              Recusar
            </StyledButton>

            <StyledButton
              color={colors.warning}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={aprovando}
              onClick={handleEditar}
            >
              Editar
            </StyledButton>
          </Box>
        </CardContent>

        <DialogRecusaCadastro
          open={recusaAberta}
          tipo={tipo}
          cadastroId={id}
          onClose={() => setRecusaAberta(false)}
          onRecusado={() => navigate(-1)}
        />

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: "16px",
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "8px",
              background: colors.primary,
            }}
          >
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {documentoUrl && (
            <iframe
              src={documentoUrl}
              width="100%"
              height="600px"
              title="Documento"
              style={{ border: "none" }}
            />
          )}
        </Dialog>
      </StyledCard>

      <AnimatePresence>
        {isEditing && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "var(--sear-superficie-suave, rgba(0,0,0,0.5))",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
              overflow: "auto",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              style={{
                backgroundColor: "var(--sear-superficie, white)",
                padding: "2rem",
                borderRadius: "16px",
                width: "90%",
                maxWidth: "700px",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 15px 50px rgba(0, 0, 0, 0.3)",
              }}
            >
              <EdicaoInfo
                dados={mantenedora}
                onSave={handleSave}
                onClose={() => setIsEditing(false)}
              />
            </motion.div>
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default MantenedoraInfo;
